/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ConditionValue,
  ConditionValue$inboundSchema,
  ConditionValue$Outbound,
  ConditionValue$outboundSchema,
} from "./conditionvalue.js";
import {
  Ref,
  Ref$inboundSchema,
  Ref$Outbound,
  Ref$outboundSchema,
} from "./ref.js";

/**
 * The comparison method used to evaluate the condition.
 */
export const Comparison = {
  LessThan: "less_than",
  LessThanOrEqualTo: "less_than_or_equal_to",
  EqualTo: "equal_to",
  GreaterThanOrEqualTo: "greater_than_or_equal_to",
  GreaterThan: "greater_than",
  NotEqualTo: "not_equal_to",
  IsNotNilOrEmpty: "is_not_nil_or_empty",
  IsNilOrEmpty: "is_nil_or_empty",
  Contains: "contains",
  StartsWith: "starts_with",
  EndsWith: "ends_with",
  IsInSequencerObj: "is_in_sequencer_obj",
  IsUnderEaiControl: "is_under_eai_control",
  Some: "some",
  Every: "every",
  IsTrimAtCurrentStage: "is_trim_at_current_stage",
} as const;
/**
 * The comparison method used to evaluate the condition.
 */
export type Comparison = OpenEnum<typeof Comparison>;

/**
 * Defines a single condition that compares a source value against a target value.
 */
export type Condition = {
  /**
   * The comparison method used to evaluate the condition.
   */
  comparison: Comparison;
  /**
   * Optional name for the condition, useful for debugging and documentation.
   */
  name?: string | undefined;
  /**
   * Negate the condition. If true, the condition is true if the condition is false and vice versa.
   */
  negate?: boolean | undefined;
  /**
   * A reference to a reading or setting.
   */
  ref?: Ref | undefined;
  /**
   * A reference to a reading or setting.
   */
  secondaryRef?: Ref | undefined;
  /**
   * Optional tolerance value for numeric comparisons to handle sensor noise. Only applicable for numeric comparisons.
   */
  tolerance?: number | undefined;
  /**
   * A value used in a condition
   */
  value: ConditionValue;
};

/** @internal */
export const Comparison$inboundSchema: z.ZodType<
  Comparison,
  z.ZodTypeDef,
  unknown
> = openEnums.inboundSchema(Comparison);
/** @internal */
export const Comparison$outboundSchema: z.ZodType<
  string,
  z.ZodTypeDef,
  Comparison
> = openEnums.outboundSchema(Comparison);

/** @internal */
export const Condition$inboundSchema: z.ZodType<
  Condition,
  z.ZodTypeDef,
  unknown
> = z.object({
  comparison: Comparison$inboundSchema,
  name: types.optional(types.string()),
  negate: types.boolean().default(false),
  ref: types.optional(Ref$inboundSchema),
  secondary_ref: types.optional(Ref$inboundSchema),
  tolerance: types.optional(types.number()),
  value: ConditionValue$inboundSchema,
}).transform((v) => {
  return remap$(v, {
    "secondary_ref": "secondaryRef",
  });
});
/** @internal */
export type Condition$Outbound = {
  comparison: string;
  name?: string | undefined;
  negate: boolean;
  ref?: Ref$Outbound | undefined;
  secondary_ref?: Ref$Outbound | undefined;
  tolerance?: number | undefined;
  value: ConditionValue$Outbound;
};

/** @internal */
export const Condition$outboundSchema: z.ZodType<
  Condition$Outbound,
  z.ZodTypeDef,
  Condition
> = z.object({
  comparison: Comparison$outboundSchema,
  name: z.string().optional(),
  negate: z.boolean().default(false),
  ref: Ref$outboundSchema.optional(),
  secondaryRef: Ref$outboundSchema.optional(),
  tolerance: z.number().optional(),
  value: ConditionValue$outboundSchema,
}).transform((v) => {
  return remap$(v, {
    secondaryRef: "secondary_ref",
  });
});

export function conditionToJSON(condition: Condition): string {
  return JSON.stringify(Condition$outboundSchema.parse(condition));
}
export function conditionFromJSON(
  jsonString: string,
): SafeParseResult<Condition, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => Condition$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'Condition' from JSON`,
  );
}
